import type { HTMLAttributes } from 'react';
import React from 'react';
import { z } from 'zod';
import classNames from 'classnames';
import config from 'config';
import Grid from '$components/layout/Grid';
import GoogleMap from '$components/basic/GoogleMap';
import { HeadingLevels } from '$src-components/atoms/HeadingLevels';
import type { ContentComponent } from '$cms/ContentComponent';
import { CmsShortTextSchema } from '$cms/types';
import type { SchemaType } from '$util/schema';
import { getLandingUrlFromLocation } from '$util/getLandingUrlFromLocation';
import { useAppContext } from '$util/AppContext';
import { useCmsPageContext } from '$cms/CmsPageContext';
import { renderRichTextContent } from '@/cms/richTextContentHelpers';
import { Styled } from './styled';

const { Row, Col } = Grid;

export interface LocationMapSectionProps extends HTMLAttributes<HTMLDivElement> {
  readonly option?: string;
}

const LocationMapSectionSchema = {
  translatedEntities: z.object({
    heading: CmsShortTextSchema,
    secondHeading: CmsShortTextSchema.optional(),
  }),
};

export type LocationMapSectionTranslatedEntities = SchemaType<
  (typeof LocationMapSectionSchema)['translatedEntities']
>;

export const LocationMapSection: ContentComponent<
  LocationMapSectionTranslatedEntities,
  undefined,
  LocationMapSectionProps
> = function LocationMapSection({
  translatedEntities,
  serverSideData,
  className,
  ...props
}): JSX.Element | null {
  const { location } = useAppContext();
  const cmsPageContext = useCmsPageContext();
  const landingUrl = getLandingUrlFromLocation(location);

  const area = cmsPageContext?.pageRoutes?.[0]?.collectionItems?.area;
  const areaName = area?.name;
  const mapAreas = [
    {
      geoMeta: area?.centerLocation.join(';'),
      name: areaName,
    },
  ];
  const areaShortDescription = area?.shortDescription;

  return (
    (area && (
      <Styled className={classNames(LocationMapSection.displayName, className)} {...props}>
        <Row>
          <Col md={{ span: 5, offset: 1 }} xs={{ span: 12, offset: 0 }}>
            <HeadingLevels className="heading" semanticLevel={4} styleLevel={3}>
              {translatedEntities.heading.value}
            </HeadingLevels>
            {translatedEntities?.secondHeading?.value && (
              <HeadingLevels className="second-heading" semanticLevel={4} styleLevel={3}>
                {translatedEntities?.secondHeading?.value}
              </HeadingLevels>
            )}
            {areaShortDescription && renderRichTextContent(areaShortDescription)}
          </Col>
          <Col md={{ span: 5 }} xs={{ span: 12 }}>
            {/**
             * @TODO rebuild GoogleMap component to utilise CMS collections
             *
             * Currently the areas are static data file, with the mapLocation used to find correct center location coords,
             * to be replaced with the current area from the collection once rebuilt
             * */}
            <GoogleMap
              areas={mapAreas}
              location={areaName}
              image="https://res.cloudinary.com/fxtr/image/upload/v1623863282/landing/location/static-gmap_sbnbe3.png"
              apiKey={`${config.get('public.gMaps.apiKey')}`}
              landingUrl={`${landingUrl}`}
            />
          </Col>
        </Row>
      </Styled>
    )) ||
    null
  );
};

LocationMapSection.displayName = 'LocationMapSection';
LocationMapSection.schema = LocationMapSectionSchema;
